/**
 * Playback speed for the forecast sea animation.
 *
 * Speed is kept off React state so cycling it never re-renders the canvas.
 * A module-level value is shared across day panes: the swipe pager keeps
 * adjacent days mounted, and each pane would otherwise reset to 1x.
 */

import type { MouseEvent } from 'react';

export const SPEED_CYCLE = [1, 2, 5] as const;

export type PlaybackSpeed = (typeof SPEED_CYCLE)[number];

let playbackSpeed: PlaybackSpeed = SPEED_CYCLE[0];

export const getPlaybackSpeed = (): PlaybackSpeed => playbackSpeed;

export const setPlaybackSpeed = (speed: PlaybackSpeed): void => {
  playbackSpeed = speed;
};

export const nextPlaybackSpeed = (current: number): PlaybackSpeed => {
  const currentIndex = SPEED_CYCLE.indexOf(current as PlaybackSpeed);

  return SPEED_CYCLE[
    currentIndex >= 0 ? (currentIndex + 1) % SPEED_CYCLE.length : 0
  ];
};

const applyPlaybackSpeedToWrap = (
  wrap: Element,
  speed: PlaybackSpeed,
): void => {
  const button = wrap.querySelector<HTMLButtonElement>('[data-speed]');

  if (button != null) {
    button.dataset.speed = String(speed);
    button.setAttribute('aria-label', `Playback speed ${speed}x`);

    const label = button.querySelector('[data-speed-label]');

    if (label != null) {
      label.textContent = `${speed}x`;
    }
  }

  const host =
    wrap.querySelector<HTMLElement>('.forecast-animation') ??
    wrap.querySelector<HTMLElement>('forecast-animation');

  host?.setAttribute('speed', String(speed));
};

export const applyPlaybackSpeed = (
  wrap: Element | null,
  speed: PlaybackSpeed = playbackSpeed,
): void => {
  if (wrap == null) {
    return;
  }

  applyPlaybackSpeedToWrap(wrap, speed);
};

export const applyPlaybackSpeedToAll = (
  speed: PlaybackSpeed = playbackSpeed,
): void => {
  document
    .querySelectorAll('[data-anim-wrap]')
    .forEach((wrap) => applyPlaybackSpeedToWrap(wrap, speed));
};

/**
 * Cycle 1x → 2x → 5x on the DOM only. Updates every mounted animation so
 * changing days keeps the speed the user already chose.
 */
export const cyclePlaybackSpeed = (
  event: MouseEvent<HTMLButtonElement>,
): void => {
  const button = event.currentTarget;
  const wrap = button.closest('[data-anim-wrap]');

  if (wrap == null) {
    return;
  }

  const next = nextPlaybackSpeed(Number(button.dataset.speed) || 1);

  setPlaybackSpeed(next);
  applyPlaybackSpeedToAll(next);
};
